import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Alert, Button, Form, Modal, Spinner} from 'react-bootstrap'
import {useLoadGoogleMaps} from '../hooks/useLoadGoogleMaps'
import {PlaceSelection, AddressComponent} from './GooglePlacesAutocompleteInput'

interface AddressSearchModalProps {
  show: boolean
  onClose: () => void
  onSelect: (selection: PlaceSelection) => void
  initialAddress?: string
  modalTitle?: string
}

const MAP_DEFAULT_CENTER = {lat: 37.4221, lng: -122.0841}

const getGoogle = () => (window as typeof window & {google?: any}).google

export const AddressSearchModal = ({
  show,
  onClose,
  onSelect,
  initialAddress,
  modalTitle = 'Buscar dirección',
}: AddressSearchModalProps) => {
  const {isLoaded, loadError} = useLoadGoogleMaps({language: 'es'})
  const inputRef = useRef<HTMLInputElement | null>(null)
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapInstanceRef = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const autocompleteRef = useRef<any>(null)
  const selectionRef = useRef<PlaceSelection | null>(null)
  const [isInitializing, setIsInitializing] = useState(false)
  const [geocodeError, setGeocodeError] = useState<string | null>(null)

  const resetSelection = useCallback(() => {
    selectionRef.current = null
    setGeocodeError(null)
  }, [])

  const handlePlaceChanged = useCallback(() => {
    const autocomplete = autocompleteRef.current
    const mapInstance = mapInstanceRef.current
    const marker = markerRef.current
    const input = inputRef.current
    const googleApi = getGoogle()

    if (!autocomplete || !mapInstance || !marker || !googleApi) return

    const place = autocomplete.getPlace?.()

    if (!place) {
      return
    }

    const formattedAddress = place.formatted_address ?? place.name ?? input?.value

    const resolveLatLng = (location: any) => {
      if (!location) return undefined
      if (typeof location.lat === 'function' && typeof location.lng === 'function') {
        return {lat: location.lat(), lng: location.lng()}
      }
      if (typeof location.lat === 'number' && typeof location.lng === 'number') {
        return {lat: location.lat, lng: location.lng}
      }
      return undefined
    }

    const location = place.geometry?.location
    const latLng = resolveLatLng(location)

    if (!latLng) {
      window.alert(`No details available for input: '${place.name ?? formattedAddress ?? ''}'`)
      return
    }

    mapInstance.setCenter(latLng)
    mapInstance.setZoom(17)
    marker.setPosition(latLng)

    const selection: PlaceSelection = {
      address: formattedAddress ?? input?.value,
      placeId: place.place_id,
      latLng,
      addressComponents: place.address_components as AddressComponent[] | undefined,
    }

    selectionRef.current = selection
    setGeocodeError(null)
  }, [])

  const initializeMap = useCallback(() => {
    if (!show || !isLoaded || autocompleteRef.current || !mapContainerRef.current || !inputRef.current) {
      return
    }

    const googleApi = getGoogle()
    if (!googleApi?.maps?.places) return

    setIsInitializing(true)
    let isActive = true

    const map = new googleApi.maps.Map(mapContainerRef.current, {
      center: MAP_DEFAULT_CENTER,
      zoom: 11,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
    })
    mapInstanceRef.current = map

    const marker = new googleApi.maps.Marker({
      map,
      position: MAP_DEFAULT_CENTER,
    })
    markerRef.current = marker
    const autocomplete = new googleApi.maps.places.Autocomplete(inputRef.current, {
      fields: ['address_components', 'formatted_address', 'geometry', 'name', 'place_id'],
      types: ['address'],
    })
    autocompleteRef.current = autocomplete
    const listener = autocomplete.addListener('place_changed', handlePlaceChanged)

    if (initialAddress?.trim()) {
      const addressToGeocode = initialAddress.trim()
      if (inputRef.current) {
        inputRef.current.value = addressToGeocode
      }
      if (googleApi.maps.Geocoder) {
        const geocoder = new googleApi.maps.Geocoder()
        geocoder.geocode({address: addressToGeocode}, (results: any, status: string) => {
          if (!isActive) return
          if (status === 'OK' && results?.[0]?.geometry?.location) {
            const location = results[0].geometry.location
            const latLng = {lat: location.lat(), lng: location.lng()}
            map.setCenter(latLng)
            map.setZoom(17)
            marker.setPosition(latLng)
            selectionRef.current = {
              address: results[0].formatted_address ?? addressToGeocode,
              placeId: results[0].place_id,
              latLng,
              addressComponents: results[0].address_components as AddressComponent[] | undefined,
            }
            setGeocodeError(null)
          } else {
            setGeocodeError('Unable to locate the provided address. Try searching again.')
          }
        })
      }
    } else if (inputRef.current) {
      inputRef.current.value = ''
    }

    setIsInitializing(false)

    return () => {
      isActive = false
      listener.remove()
      marker.setMap(null)
      markerRef.current = null
      autocompleteRef.current = null
      mapInstanceRef.current = null
    }
  }, [handlePlaceChanged, initialAddress, isLoaded, show])

  const handleConfirm = useCallback(() => {
    const selection = selectionRef.current
    if (!selection) {
      if (!inputRef.current?.value) {
        setGeocodeError('Please search and select an address before continuing.')
        return
      }
      setGeocodeError('Unable to confirm address. Please choose a suggestion from the list.')
      return
    }
    onSelect(selection)
    onClose()
  }, [onClose, onSelect])

  useEffect(() => {
    if (!show) return
    resetSelection()
    if (inputRef.current) {
      inputRef.current.value = initialAddress ?? ''
    }
  }, [initialAddress, resetSelection, show])

  useEffect(() => {
    if (!show) return
    const cleanup = initializeMap()
    return () => {
      cleanup?.()
    }
  }, [initializeMap, show])

  const modalBody = useMemo(() => {
    if (loadError) {
      return <Alert variant='danger'>Unable to load Google Maps. Please try again later.</Alert>
    }

    if (!isLoaded || isInitializing) {
      return (
        <div
          className='d-flex justify-content-center align-items-center'
          style={{minHeight: '280px'}}
        >
          <Spinner animation='border' role='status' />
        </div>
      )
    }

    return (
      <>
        <Form.Group className='mb-3'>
          <Form.Label>Buscar</Form.Label>
          <Form.Control
            ref={inputRef}
            type='text'
            placeholder='Escribe una dirección'
            autoComplete='off'
          />
        </Form.Group>
        <div
          ref={mapContainerRef}
          style={{minHeight: '320px', width: '100%'}}
          className='rounded border'
        />
        {geocodeError && (
          <Alert className='mt-3 mb-0' variant='warning'>
            {geocodeError}
          </Alert>
        )}
      </>
    )
  }, [geocodeError, isInitializing, isLoaded, loadError])

  return (
    <Modal show={show} onHide={onClose} centered size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{modalBody}</Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          Cancelar
        </Button>
        <Button variant='primary' onClick={handleConfirm}>
          Usar dirección
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
